import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Layout } from "../components/Layout";
import { VerifyBadge, CustomBadge } from "../components/Badges";
import api from "../lib/api";
import {
  Briefcase, MagnifyingGlass, User, VideoCamera, PencilSimple,
  Plus, ArrowRight, ChartBar, FileMagnifyingGlass,
} from "@phosphor-icons/react";

function StatCard({ label, value, icon: Icon, tid }) {
  return (
    <div className="as-card p-5" data-testid={tid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-widest text-[var(--as-text-secondary)]">{label}</span>
        <Icon size={22} weight="duotone" color="#080F14" />
      </div>
      <div className="text-3xl font-extrabold mt-2 text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [media, setMedia] = useState([]);
  const [myApps, setMyApps] = useState([]);
  const [myOps, setMyOps] = useState([]);

  useEffect(() => {
    if (!user) return;
    if (user.role === "skilled_individual" || user.role === "msme") {
      api.get(`/media/${user.id}`).then(({ data }) => setMedia(data));
      api.get("/applications/mine").then(({ data }) => setMyApps(data));
    }
    if (user.role === "employer" || user.role === "msme") {
      api.get(`/opportunities?posted_by=${user.id}&status=`).then(({ data }) => setMyOps(data));
    }
  }, [user]);

  if (!user) return null;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--as-accent)]">{t("welcome_back")}</div>
            <h1 className="text-3xl font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{user.name}</h1>
            <div className="flex items-center gap-2 mt-2">
              <VerifyBadge status={user.kyc_status} />
              {user.badges?.map((b) => <CustomBadge key={b} label={b} />)}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/profile/${user.id}`} className="btn-secondary !py-3 !px-5" data-testid="dash-view-profile">
              <User size={18} weight="duotone" /> View profile
            </Link>
            <Link to="/profile/edit" className="btn-primary !py-3 !px-5" data-testid="dash-edit-profile">
              <PencilSimple size={18} weight="duotone" /> {t("edit_profile")}
            </Link>
          </div>
        </div>

        {(user.role === "skilled_individual" || user.role === "msme") && (
          <>
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <StatCard label={t("media_gallery")} value={media.length} icon={VideoCamera} tid="stat-media" />
              <StatCard label={t("my_applications")} value={myApps.length} icon={FileMagnifyingGlass} tid="stat-apps" />
              <StatCard label={t("status")} value={user.kyc_status} icon={ChartBar} tid="stat-status" />
            </div>

            <div className="as-card p-7 mb-8">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{t("media_gallery")}</h2>
                <Link to="/profile/edit" className="btn-ghost" data-testid="dash-upload-link">
                  <Plus size={18} weight="bold" /> {t("cta_upload_video")}
                </Link>
              </div>
              {media.length === 0 ? (
                <p className="text-[var(--as-text-secondary)]">{t("no_media")}</p>
              ) : (
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {media.map((m) => (
                    <div key={m.id} className="as-card !p-0 overflow-hidden">
                      {m.type === "image" ? (
                        <img src={m.data_url} alt={m.title} className="w-full h-40 object-cover" />
                      ) : (
                        <video src={m.data_url} controls className="w-full h-40 object-cover bg-black" />
                      )}
                      <div className="p-3">
                        <div className="font-bold text-sm">{m.title}</div>
                        <div className="text-xs text-[var(--as-text-secondary)] line-clamp-2">{m.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="as-card p-7">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{t("my_applications")}</h2>
                <Link to="/opportunities" className="btn-ghost" data-testid="dash-find-jobs">
                  <Briefcase size={18} weight="duotone" /> {t("search_jobs")}
                </Link>
              </div>
              {myApps.length === 0 ? (
                <p className="text-[var(--as-text-secondary)]">{t("no_results")}</p>
              ) : (
                <ul className="divide-y divide-[var(--as-border)]">
                  {myApps.map((a) => (
                    <li key={a.id} className="py-3 flex items-center justify-between gap-3" data-testid={`app-row-${a.id}`}>
                      <Link to={`/opportunities/${a.opportunity_id}`} className="font-bold text-[var(--as-primary)] hover:underline">
                        {a.opportunity_id.slice(0, 8)}…
                      </Link>
                      <span className="as-badge-pill">{a.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        {user.role === "employer" && (
          <>
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <StatCard label={t("my_opportunities")} value={myOps.length} icon={Briefcase} tid="stat-myops" />
              <StatCard label={t("op_open")} value={myOps.filter((o) => o.status === "open").length} icon={ChartBar} tid="stat-open" />
              <StatCard label={t("status")} value={user.kyc_status} icon={User} tid="stat-status" />
            </div>

            <div className="flex flex-wrap gap-3 mb-8">
              <Link to="/opportunities/new" className="btn-primary" data-testid="dash-new-op">
                <Plus size={18} weight="bold" /> {t("new_opportunity")}
              </Link>
              <Link to="/search" className="btn-secondary" data-testid="dash-search-talent">
                <MagnifyingGlass size={18} weight="duotone" /> {t("search_talent")}
              </Link>
            </div>

            <div className="as-card p-7">
              <h2 className="text-xl font-bold text-[var(--as-primary)] mb-5" style={{fontFamily:"Inter"}}>{t("my_opportunities")}</h2>
              {myOps.length === 0 ? (
                <p className="text-[var(--as-text-secondary)]">{t("no_results")}</p>
              ) : (
                <ul className="divide-y divide-[var(--as-border)]">
                  {myOps.map((o) => (
                    <li key={o.id} className="py-3 flex items-center justify-between gap-3" data-testid={`my-op-${o.id}`}>
                      <div>
                        <Link to={`/opportunities/${o.id}`} className="font-bold text-[var(--as-primary)] hover:underline">{o.title}</Link>
                        <div className="text-xs text-[var(--as-text-secondary)]">{o.type} · {o.city || "Remote"}</div>
                      </div>
                      <span className="as-badge-pill">{o.status}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        {user.role === "admin" && (
          <Link to="/admin" className="btn-primary" data-testid="dash-admin-link">
            Open admin <ArrowRight size={18} weight="bold" />
          </Link>
        )}

        {user.role === "sponsor" && (
          <Link to="/sponsor/dashboard" className="btn-primary" data-testid="dash-sponsor-link">
            Open sponsor dashboard <ArrowRight size={18} weight="bold" />
          </Link>
        )}
      </div>
    </Layout>
  );
}
